import React, { useState, useEffect, useCallback } from 'react';
import { teamApi, userApi, projectApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Table, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, UsersThree, UserPlus, UserMinus } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['civil', 'mechanical', 'electrical', 'plumbing', 'general'];

export default function TeamsPage() {
  const { user: currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: 'civil', leader_id: '', project_id: '', member_ids: []
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Member modal
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [teamsRes, usersRes, projectsRes] = await Promise.all([
        teamApi.list(filterProject || undefined),
        userApi.list(),
        projectApi.list()
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const getUserName = (id) => users.find(u => u.id === id)?.name || 'Unknown';
  const getProjectName = (id) => projects.find(p => p.id === id)?.name || '-';

  // ---- Create / Edit ----
  const openCreateModal = () => {
    setEditingTeam(null);
    setFormData({ name: '', category: 'civil', leader_id: '', project_id: filterProject, member_ids: [] });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      category: team.category,
      leader_id: team.leader_id || '',
      project_id: team.project_id || '',
      member_ids: team.member_ids || []
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingTeam) {
        await teamApi.update(editingTeam.id, formData);
      } else {
        await teamApi.create(formData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete (soft) ----
  const handleDelete = async (teamId, teamName) => {
    if (!window.confirm(`Deactivate team "${teamName}"? This will soft-delete the team.`)) return;
    try {
      await teamApi.delete(teamId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete team');
    }
  };

  // ---- Members ----
  const openMemberModal = (team) => {
    setSelectedTeam(team);
    setShowMemberModal(true);
  };

  const handleAddMember = async (memberId) => {
    try {
      await teamApi.addMember(selectedTeam.id, memberId);
      const updated = (await teamApi.get(selectedTeam.id)).data;
      setSelectedTeam(updated);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await teamApi.removeMember(selectedTeam.id, memberId);
      const updated = (await teamApi.get(selectedTeam.id)).data;
      setSelectedTeam(updated);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const canDelete = currentUser?.role === 'IT Admin' || currentUser?.role === 'Project Manager';

  const columns = [
    {
      header: 'Team',
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-[#FFAB00] rounded-lg flex items-center justify-center shrink-0">
            <UsersThree className="w-5 h-5 text-white" weight="bold" />
          </div>
          <div>
            <p className="font-medium text-[#0A0A0A]" data-testid={`team-name-cell-${row.id}`}>{row.name}</p>
            <p className="text-xs text-[#666666]">{row.member_ids?.length || 0} member{(row.member_ids?.length || 0) !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Category',
      render: (row) => (
        <Badge variant="primary" data-testid={`team-category-cell-${row.id}`}>
          {row.category?.charAt(0).toUpperCase() + row.category?.slice(1)}
        </Badge>
      )
    },
    {
      header: 'Leader',
      render: (row) => (
        <span data-testid={`team-leader-cell-${row.id}`}>
          {row.leader_id ? getUserName(row.leader_id) : <span className="text-[#999]">-</span>}
        </span>
      )
    },
    {
      header: 'Project',
      render: (row) => (
        <span data-testid={`team-project-cell-${row.id}`}>
          {row.project_id ? getProjectName(row.project_id) : <span className="text-[#999]">Unassigned</span>}
        </span>
      )
    },
    {
      header: 'Status',
      render: (row) => (
        <Badge variant={row.is_active !== false ? 'success' : 'danger'} data-testid={`team-status-cell-${row.id}`}>
          {row.is_active !== false ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => openMemberModal(row)}
            className="p-1.5 rounded text-[#666666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
            title="Manage Members"
            data-testid={`manage-members-btn-${row.id}`}
          >
            <UserPlus className="w-4 h-4" />
          </button>
          <button
            onClick={() => openEditModal(row)}
            className="p-1.5 rounded text-[#666666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
            title="Edit Team"
            data-testid={`edit-team-btn-${row.id}`}
          >
            <PencilSimple className="w-4 h-4" />
          </button>
          {canDelete && (
            <button
              onClick={() => handleDelete(row.id, row.name)}
              className="p-1.5 rounded text-[#666666] hover:text-[#FF3333] hover:bg-[#FF3333]/5 transition-colors"
              title="Deactivate Team"
              data-testid={`delete-team-btn-${row.id}`}
            >
              <Trash className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    },
  ];

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Teams</h1>
          <p className="text-[#666666]">Manage project teams and members</p>
        </div>
        <Button onClick={openCreateModal} data-testid="add-team-btn">
          <Plus className="w-4 h-4 mr-2" /> New Team
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between">
        <Select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          className="w-64"
          data-testid="filter-project-select"
        >
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <span className="text-sm text-[#666666]" data-testid="team-count">
          {teams.length} team{teams.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <Card>
        <Table columns={columns} data={teams} />
      </Card>

      {/* Create / Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingTeam ? 'Edit Team' : 'Create Team'}>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="team-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="team-form-error">{error}</div>}

          <Input
            label="Team Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            data-testid="team-name-input"
          />

          <Select
            label="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            data-testid="team-category-select"
          >
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </Select>

          <Select
            label="Team Leader"
            value={formData.leader_id}
            onChange={(e) => setFormData({ ...formData, leader_id: e.target.value })}
            data-testid="team-leader-select"
          >
            <option value="">-- Select Leader --</option>
            {users.filter(u => u.is_active !== false).map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
            ))}
          </Select>

          <Select
            label="Project"
            value={formData.project_id}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            data-testid="team-project-select"
          >
            <option value="">-- No Project --</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-team-btn">
              {saving ? 'Saving...' : editingTeam ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Members Modal */}
      <Modal isOpen={showMemberModal} onClose={() => setShowMemberModal(false)} title={`Members \u2014 ${selectedTeam?.name || ''}`}>
        <div className="space-y-5" data-testid="member-modal-content">
          {/* Current members */}
          <div>
            <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">
              Current Members ({selectedTeam?.member_ids?.length || 0})
            </h4>
            {selectedTeam?.member_ids?.length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedTeam.member_ids.map(mid => {
                  const member = users.find(u => u.id === mid);
                  return (
                    <div key={mid} className="flex items-center justify-between p-2.5 bg-[#F9F9F9] rounded-lg" data-testid={`current-member-${mid}`}>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-[#002FA7] rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{(member?.name || '?')[0]}</span>
                        </div>
                        <span className="text-sm font-medium">{member?.name || 'Unknown'}</span>
                        <span className="text-xs text-[#666666]">({member?.role || '?'})</span>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(mid)}
                        className="p-1 text-[#FF3333] hover:bg-[#FF3333]/10 rounded transition-colors"
                        title="Remove member"
                        data-testid={`remove-member-btn-${mid}`}
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#666666] py-2">No members yet</p>
            )}
          </div>

          {/* Available users to add */}
          <div>
            <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">Add Member</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {users
                .filter(u => u.is_active !== false && !selectedTeam?.member_ids?.includes(u.id))
                .map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2.5 bg-[#F9F9F9] rounded-lg" data-testid={`available-member-${u.id}`}>
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-[#E5E5E5] rounded-full flex items-center justify-center">
                        <span className="text-[10px] font-bold text-[#666]">{u.name[0]}</span>
                      </div>
                      <span className="text-sm">{u.name}</span>
                      <span className="text-xs text-[#666666]">({u.role})</span>
                    </div>
                    <button
                      onClick={() => handleAddMember(u.id)}
                      className="p-1 text-[#002FA7] hover:bg-[#002FA7]/10 rounded transition-colors"
                      title="Add to team"
                      data-testid={`add-member-btn-${u.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              {users.filter(u => u.is_active !== false && !selectedTeam?.member_ids?.includes(u.id)).length === 0 && (
                <p className="text-sm text-[#666666] py-2 text-center">All users are already members</p>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
